import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Check, X, AlertCircle, Settings, Mic, Users, Mail } from 'lucide-react';

interface MCPServer {
  id: string;
  server_type: string;
  server_url: string;
  connection_status: string;
  configuration: any;
  last_connected_at?: string;
  error_message?: string;
  user_id: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
}

const serverConfigs = {
  elevenlabs: {
    name: 'ElevenLabs Voice',
    description: 'Text-to-speech and voice synthesis for AI-powered interviews',
    icon: Mic,
    defaultUrl: 'http://localhost:3000/sse',
    requiredCredentials: ['ELEVENLABS_API_KEY']
  },
  linkedin: {
    name: 'LinkedIn Outreach',
    description: 'Automated candidate sourcing and messaging',
    icon: Users,
    defaultUrl: 'http://localhost:3001/sse',
    requiredCredentials: ['LINKEDIN_USERNAME', 'LINKEDIN_PASSWORD']
  },
  'microsoft-exchange': {
    name: 'Microsoft Exchange',
    description: 'Email integration and calendar management',
    icon: Mail,
    defaultUrl: 'http://localhost:3002/sse',
    requiredCredentials: ['MICROSOFT_CLIENT_ID', 'MICROSOFT_CLIENT_SECRET']
  }
};

const MCPServerConfig: React.FC = () => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<string | null>(null);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const { data, error } = await supabase
        .from('mcp_server_connections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setServers(data || []);
    } catch (error) {
      console.error('Error fetching MCP servers:', error);
      toast.error('Failed to load MCP server configurations');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (serverId: string, serverType: string, serverUrl: string) => {
    setConnecting(serverId);
    try {
      const { data, error } = await supabase.functions.invoke('mcp-server-config', {
        body: {
          action: 'test_connection',
          serverId,
          serverType,
          serverUrl
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Successfully connected to ${serverConfigs[serverType as keyof typeof serverConfigs].name}`);
        await fetchServers(); // Refresh to get updated status
      } else {
        toast.error(data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error('Failed to test MCP server connection');
    } finally {
      setConnecting(null);
    }
  };

  const updateServerConfig = async (serverId: string, updates: Partial<MCPServer>) => {
    try {
      const { error } = await supabase
        .from('mcp_server_connections')
        .update(updates)
        .eq('id', serverId);

      if (error) throw error;
      
      await fetchServers();
      toast.success('Server configuration updated');
    } catch (error) {
      console.error('Error updating server config:', error);
      toast.error('Failed to update server configuration');
    }
  };

  const createServer = async (serverType: keyof typeof serverConfigs) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      const { error } = await supabase
        .from('mcp_server_connections')
        .insert({
          user_id: user.id,
          organization_id: profile?.organization_id,
          server_type: serverType,
          server_url: serverConfigs[serverType].defaultUrl,
          configuration: {}
        });

      if (error) throw error;
      
      await fetchServers();
      toast.success(`${serverConfigs[serverType].name} server added`);
    } catch (error) {
      console.error('Error creating server:', error);
      toast.error('Failed to create MCP server');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      connected: { variant: 'default' as const, icon: Check, color: 'text-green-600' },
      disconnected: { variant: 'secondary' as const, icon: X, color: 'text-gray-500' },
      error: { variant: 'destructive' as const, icon: AlertCircle, color: 'text-red-600' },
      connecting: { variant: 'outline' as const, icon: Loader2, color: 'text-blue-600' }
    };

    const config = variants[status as keyof typeof variants] || variants.disconnected;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color} ${status === 'connecting' ? 'animate-spin' : ''}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading MCP servers...</span>
      </div>
    );
  }

  const existingServerTypes = new Set(servers.map(s => s.server_type));
  const availableServerTypes = Object.keys(serverConfigs).filter(
    type => !existingServerTypes.has(type as any)
  ) as (keyof typeof serverConfigs)[];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">MCP Server Connections</h3>
          <p className="text-sm text-muted-foreground">
            Configure Model Context Protocol servers for enhanced AI agent capabilities
          </p>
        </div>
      </div>

      {/* Add New Server Cards */}
      {availableServerTypes.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {availableServerTypes.map((serverType) => {
            const config = serverConfigs[serverType];
            const Icon = config.icon;
            
            return (
              <Card key={serverType} className="border-dashed">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">{config.name}</CardTitle>
                  </div>
                  <CardDescription className="text-xs">
                    {config.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => createServer(serverType)}
                    className="w-full"
                    size="sm"
                  >
                    Add {config.name}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Existing Server Configurations */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {servers.map((server) => {
          const config = serverConfigs[server.server_type];
          const Icon = config.icon;
          
          return (
            <Card key={server.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">{config.name}</CardTitle>
                  </div>
                  {getStatusBadge(server.connection_status)}
                </div>
                <CardDescription className="text-xs">
                  {config.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`url-${server.id}`} className="text-xs">Server URL</Label>
                  <Input
                    id={`url-${server.id}`}
                    value={server.server_url}
                    onChange={(e) => updateServerConfig(server.id, { server_url: e.target.value })}
                    placeholder={config.defaultUrl}
                    className="text-xs"
                  />
                </div>

                {server.error_message && (
                  <div className="p-2 bg-destructive/10 rounded-md">
                    <p className="text-xs text-destructive">{server.error_message}</p>
                  </div>
                )}

                {server.last_connected_at && (
                  <p className="text-xs text-muted-foreground">
                    Last connected: {new Date(server.last_connected_at).toLocaleString()}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={() => testConnection(server.id, server.server_type, server.server_url)}
                    disabled={connecting === server.id}
                    size="sm"
                    className="flex-1"
                  >
                    {connecting === server.id ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Test Connection
                  </Button>
                  <Button variant="outline" size="sm">
                    <Settings className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {servers.length === 0 && availableServerTypes.length === 0 && (
        <div className="text-center py-8">
          <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold mb-2">All MCP Servers Configured</h4>
          <p className="text-muted-foreground">
            Your MCP server connections are ready for enhanced AI agent capabilities.
          </p>
        </div>
      )}
    </div>
  );
};

export default MCPServerConfig;
